/**
 * Director time review, modeled on Kanvo's: pick a range, see every entry with
 * its flags, fix mistakes with a reason, approve, then export the approved
 * hours for payroll.
 */

import { useMemo, useState, type FormEvent } from "react";
import { useMe } from "../auth/MeProvider";
import { useSession } from "../auth/SessionProvider";
import { inputClass } from "../components/form";
import { Button, Card, Empty, Notice, PageHeader, Tag, selectClass } from "../components/ui";
import { describeApiError } from "../lib/api";
import {
  addDays,
  formatDay,
  formatDuration,
  formatHours,
  formatTime,
  fromLocalInput,
  todayIn,
  toLocalInput,
  weekStart,
} from "../lib/format";
import { useLoad } from "../lib/useLoad";

interface ReviewEntry {
  id: string;
  userId: string;
  person: string;
  localDate: string;
  clockIn: string;
  clockOut: string | null;
  breakMinutes: number;
  paidMinutes: number | null;
  note: string | null;
  edited: boolean;
  approvedAt: string | null;
  exportedAt: string | null;
  flags: string[];
}

interface ReviewWeek {
  userId: string;
  person: string;
  weekStart: string;
  paidMinutes: number;
  regularMinutes: number;
  overtimeMinutes: number;
  regularPay: number | null;
}

interface Settings {
  overtimeEnabled: boolean;
  dailyMinutes: number | null;
  weeklyMinutes: number;
  weekStartsOn: number;
  multiplier: number;
  requireReasonOnEdit: boolean;
}

interface Review {
  timezone: string;
  /** The whole pay weeks the summary and export cover. */
  payWeeks: { from: string; to: string };
  settings: Settings;
  entries: ReviewEntry[];
  weeks: ReviewWeek[];
}

const FLAG_LABELS: Record<string, string> = {
  missed_clock_out: "Missed clock-out",
  long_shift: "Over 10 hours",
  edited: "Edited",
};

const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

/** Loads the pay week start first, so the default range is one real pay week. */
export function TimeReview() {
  const { api } = useSession();
  const settings = useLoad(() => api<{ settings: Settings }>("/api/timeclock/settings"), [api]);
  if (settings.error) return <Notice>{settings.error}</Notice>;
  if (!settings.data) return null;
  return <TimeReviewPage weekStartsOn={settings.data.settings.weekStartsOn} />;
}

function TimeReviewPage({ weekStartsOn }: { weekStartsOn: number }) {
  const { me } = useMe();
  const { api, download } = useSession();
  const timezone = me.org?.timezone ?? "America/New_York";

  const thisWeek = weekStart(todayIn(timezone), weekStartsOn);
  const [from, setFrom] = useState(thisWeek);
  const [to, setTo] = useState(addDays(thisWeek, 6));
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [editing, setEditing] = useState<ReviewEntry | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [message, setMessage] = useState<{ tone: "ok" | "error"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const { data, error, reload } = useLoad(
    () => api<Review>(`/api/timeclock/review?from=${from}&to=${to}`),
    [api, from, to],
  );

  const byPerson = useMemo(() => {
    const groups = new Map<string, ReviewEntry[]>();
    for (const entry of data?.entries ?? []) {
      const list = groups.get(entry.person) ?? [];
      list.push(entry);
      groups.set(entry.person, list);
    }
    return [...groups.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [data]);

  const approvable = (data?.entries ?? []).filter((e) => e.clockOut && !e.approvedAt);

  function shiftRange(days: number) {
    setFrom(addDays(from, days));
    setTo(addDays(to, days));
    setSelected(new Set());
  }

  function toggle(id: string) {
    setSelected((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function approve(ids: string[]) {
    if (ids.length === 0) return;
    setBusy(true);
    setMessage(null);
    try {
      const result = await api<{ approved: number }>("/api/timeclock/approve", {
        method: "POST",
        body: JSON.stringify({ ids }),
      });
      setMessage({ tone: "ok", text: `Approved ${result.approved} ${result.approved === 1 ? "entry" : "entries"}.` });
      setSelected(new Set());
      reload();
    } catch (caught) {
      setMessage({ tone: "error", text: describeApiError(caught) });
    } finally {
      setBusy(false);
    }
  }

  async function exportCsv() {
    setBusy(true);
    setMessage(null);
    try {
      const { blob, filename } = await download(`/api/timeclock/export.csv?from=${from}&to=${to}`);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename ?? `cubby-time-${from}-to-${to}.csv`;
      link.click();
      URL.revokeObjectURL(url);

      if (window.confirm("Mark the exported entries as sent to payroll? They can no longer be edited here after that.")) {
        const result = await api<{ marked: number }>("/api/timeclock/mark-exported", {
          method: "POST",
          body: JSON.stringify({ from, to }),
        });
        setMessage({ tone: "ok", text: `Marked ${result.marked} ${result.marked === 1 ? "entry" : "entries"} as sent to payroll.` });
        reload();
      }
    } catch (caught) {
      setMessage({ tone: "error", text: describeApiError(caught) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div>
      <PageHeader
        title="Time review"
        detail={`Approve hours before exporting them to payroll. Times are in ${timezone.replace(/_/g, " ")}.`}
        action={
          <Button variant="secondary" onClick={() => setShowSettings((v) => !v)}>
            {showSettings ? "Hide settings" : "Overtime settings"}
          </Button>
        }
      />

      {showSettings && data ? (
        <SettingsCard
          settings={data.settings}
          onSaved={() => {
            setShowSettings(false);
            reload();
          }}
        />
      ) : null}

      <Card className="mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <Button variant="secondary" onClick={() => shiftRange(-7)} aria-label="Previous week">
            Previous
          </Button>
          <label className="text-sm text-stone-600">
            From
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className={`${selectClass} mt-1 block`} />
          </label>
          <label className="text-sm text-stone-600">
            To
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} className={`${selectClass} mt-1 block`} />
          </label>
          <Button variant="secondary" onClick={() => shiftRange(7)} aria-label="Next week">
            Next
          </Button>
          <div className="ml-auto flex flex-wrap gap-2">
            <Button variant="secondary" disabled={busy || selected.size === 0} onClick={() => void approve([...selected])}>
              Approve selected ({selected.size})
            </Button>
            <Button variant="secondary" disabled={busy || approvable.length === 0} onClick={() => void approve(approvable.map((e) => e.id))}>
              Approve all closed ({approvable.length})
            </Button>
            <Button disabled={busy} onClick={() => void exportCsv()}>
              Export approved CSV
            </Button>
          </div>
        </div>
      </Card>

      {message ? (
        <div className="mb-6">
          <Notice tone={message.tone}>{message.text}</Notice>
        </div>
      ) : null}
      {error ? <Notice>{error}</Notice> : null}

      {data && data.weeks.length > 0 ? (
        <Card className="mb-6">
          <h2 className="text-lg font-semibold">Hours by pay week</h2>
          {data.payWeeks.from !== from || data.payWeeks.to !== to ? (
            <p className="mt-1 text-sm text-stone-500">
              Overtime is counted per pay week, so these totals and the export cover {formatDay(data.payWeeks.from)} to{" "}
              {formatDay(data.payWeeks.to)}.
            </p>
          ) : null}
          <div className="mt-4 overflow-x-auto">
            <table className="w-full min-w-[560px] text-left text-sm">
              <thead className="text-stone-500">
                <tr>
                  <th className="pb-2 font-medium">Person</th>
                  <th className="pb-2 font-medium">Week of</th>
                  <th className="pb-2 text-right font-medium">Regular</th>
                  <th className="pb-2 text-right font-medium">Overtime</th>
                  <th className="pb-2 text-right font-medium">Total</th>
                  <th className="pb-2 text-right font-medium">Straight-time pay</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100">
                {data.weeks.map((week) => (
                  <tr key={`${week.userId}-${week.weekStart}`}>
                    <td className="py-2.5">{week.person}</td>
                    <td className="py-2.5">{formatDay(week.weekStart)}</td>
                    <td className="py-2.5 text-right">{formatHours(week.regularMinutes)}</td>
                    <td className={`py-2.5 text-right ${week.overtimeMinutes > 0 ? "font-semibold text-amber-800" : ""}`}>
                      {formatHours(week.overtimeMinutes)}
                    </td>
                    <td className="py-2.5 text-right">{formatHours(week.paidMinutes)}</td>
                    <td className="py-2.5 text-right">
                      {week.regularPay === null ? <span className="text-stone-400">No rate set</span> : `$${week.regularPay.toFixed(2)}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ) : null}

      {data && byPerson.length === 0 ? <Empty>No time recorded between {formatDay(from)} and {formatDay(to)}.</Empty> : null}

      <div className="space-y-6">
        {byPerson.map(([person, entries]) => (
          <Card key={person}>
            <h2 className="text-lg font-semibold">{person}</h2>
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[640px] text-left text-sm">
                <thead className="text-stone-500">
                  <tr>
                    <th className="w-8 pb-2" />
                    <th className="pb-2 font-medium">Day</th>
                    <th className="pb-2 font-medium">In</th>
                    <th className="pb-2 font-medium">Out</th>
                    <th className="pb-2 font-medium">Break</th>
                    <th className="pb-2 font-medium">Paid</th>
                    <th className="pb-2 font-medium">Status</th>
                    <th className="pb-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {entries.map((entry) => (
                    <tr key={entry.id}>
                      <td className="py-2.5">
                        {entry.clockOut && !entry.approvedAt ? (
                          <input
                            type="checkbox"
                            aria-label={`Select ${person} ${entry.localDate}`}
                            checked={selected.has(entry.id)}
                            onChange={() => toggle(entry.id)}
                            className="h-4 w-4 accent-leaf-700"
                          />
                        ) : null}
                      </td>
                      <td className="py-2.5">{formatDay(entry.localDate)}</td>
                      <td className="py-2.5">{formatTime(entry.clockIn, timezone)}</td>
                      <td className="py-2.5">{entry.clockOut ? formatTime(entry.clockOut, timezone) : "Open"}</td>
                      <td className="py-2.5">{entry.breakMinutes ? formatDuration(entry.breakMinutes) : ""}</td>
                      <td className="py-2.5">{formatDuration(entry.paidMinutes)}</td>
                      <td className="space-x-1 py-2.5">
                        {entry.flags.map((flag) => (
                          <Tag key={flag} tone={flag === "edited" ? "neutral" : "warn"}>
                            {FLAG_LABELS[flag] ?? flag}
                          </Tag>
                        ))}
                        {entry.exportedAt ? <Tag tone="ok">Exported</Tag> : entry.approvedAt ? <Tag tone="ok">Approved</Tag> : null}
                      </td>
                      <td className="py-2.5 text-right">
                        {!entry.exportedAt ? (
                          <Button variant="ghost" size="sm" onClick={() => setEditing(entry)}>
                            Edit
                          </Button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        ))}
      </div>

      {editing && data ? (
        <EditDialog
          entry={editing}
          timezone={timezone}
          requireReason={data.settings.requireReasonOnEdit}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            setMessage({ tone: "ok", text: "Saved. The entry needs approving again." });
            reload();
          }}
        />
      ) : null}
    </div>
  );
}

function EditDialog({
  entry,
  timezone,
  requireReason,
  onClose,
  onSaved,
}: {
  entry: ReviewEntry;
  timezone: string;
  requireReason: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { api } = useSession();
  const [clockIn, setClockIn] = useState(toLocalInput(entry.clockIn, timezone));
  const [clockOut, setClockOut] = useState(toLocalInput(entry.clockOut, timezone));
  const [breakMinutes, setBreakMinutes] = useState(String(entry.breakMinutes));
  const [note, setNote] = useState(entry.note ?? "");
  const [reason, setReason] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save(event: FormEvent) {
    event.preventDefault();
    setError(null);
    const inIso = fromLocalInput(clockIn, timezone);
    const outIso = clockOut ? fromLocalInput(clockOut, timezone) : null;
    if (!inIso) return setError(clockIn ? "That clock-in time does not exist. Clocks skip an hour that night for daylight saving." : "Enter a clock-in time.");
    if (clockOut && !outIso) return setError("That clock-out time does not exist. Clocks skip an hour that night for daylight saving.");
    if (requireReason && !reason.trim()) return setError("Say why you are changing this entry.");

    setBusy(true);
    try {
      await api(`/api/timeclock/entries/${entry.id}`, {
        method: "PATCH",
        body: JSON.stringify({
          clockIn: inIso,
          clockOut: outIso,
          breakMinutes: Number.parseInt(breakMinutes || "0", 10),
          note,
          reason,
        }),
      });
      onSaved();
    } catch (caught) {
      setError(describeApiError(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-30 flex items-center justify-center bg-ink/40 px-4" role="dialog" aria-modal="true" aria-labelledby="edit-title">
      <form onSubmit={save} className="w-full max-w-lg space-y-4 rounded-2xl bg-white p-6 shadow-xl">
        <h2 id="edit-title" className="text-lg font-semibold">
          Edit {entry.person}, {formatDay(entry.localDate)}
        </h2>
        {error ? <Notice>{error}</Notice> : null}
        <div className="grid gap-4 sm:grid-cols-2">
          <label className="text-sm font-medium text-stone-700">
            Clock in
            <input type="datetime-local" required value={clockIn} onChange={(e) => setClockIn(e.target.value)} className={`${inputClass} mt-1`} />
          </label>
          <label className="text-sm font-medium text-stone-700">
            Clock out
            <input type="datetime-local" value={clockOut} onChange={(e) => setClockOut(e.target.value)} className={`${inputClass} mt-1`} />
          </label>
        </div>
        <label className="block text-sm font-medium text-stone-700">
          Unpaid break minutes
          <input type="number" min={0} max={1440} value={breakMinutes} onChange={(e) => setBreakMinutes(e.target.value)} className={`${inputClass} mt-1`} />
        </label>
        <label className="block text-sm font-medium text-stone-700">
          Note on the entry
          <input type="text" maxLength={500} value={note} onChange={(e) => setNote(e.target.value)} className={`${inputClass} mt-1`} />
        </label>
        <label className="block text-sm font-medium text-stone-700">
          Reason for the change{requireReason ? "" : " (optional)"}
          <input
            type="text"
            maxLength={500}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Forgot to clock out at 5:30"
            className={`${inputClass} mt-1`}
          />
        </label>
        <p className="text-sm text-stone-500">The original times are kept. Saving clears any approval on this entry.</p>
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={busy}>
            {busy ? "Saving" : "Save change"}
          </Button>
        </div>
      </form>
    </div>
  );
}

function SettingsCard({ settings, onSaved }: { settings: Settings; onSaved: () => void }) {
  const { api } = useSession();
  const [overtimeEnabled, setOvertimeEnabled] = useState(settings.overtimeEnabled);
  const [weeklyHours, setWeeklyHours] = useState(String(settings.weeklyMinutes / 60));
  const [dailyHours, setDailyHours] = useState(settings.dailyMinutes === null ? "" : String(settings.dailyMinutes / 60));
  const [multiplier, setMultiplier] = useState(String(settings.multiplier));
  const [weekStartsOn, setWeekStartsOn] = useState(settings.weekStartsOn);
  const [requireReason, setRequireReason] = useState(settings.requireReasonOnEdit);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api("/api/timeclock/settings", {
        method: "PUT",
        body: JSON.stringify({
          overtimeEnabled,
          weeklyMinutes: Math.round(Number(weeklyHours) * 60),
          dailyMinutes: dailyHours.trim() === "" ? null : Math.round(Number(dailyHours) * 60),
          multiplier: Number(multiplier),
          weekStartsOn,
          requireReasonOnEdit: requireReason,
        }),
      });
      onSaved();
    } catch (caught) {
      setError(describeApiError(caught));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="mb-6">
      <form onSubmit={save} className="space-y-4">
        <h2 className="text-lg font-semibold">Overtime and pay week</h2>
        {error ? <Notice>{error}</Notice> : null}
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={overtimeEnabled} onChange={(e) => setOvertimeEnabled(e.target.checked)} className="h-4 w-4 accent-leaf-700" />
          Calculate overtime
        </label>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <label className="text-sm font-medium text-stone-700">
            Weekly threshold (hours)
            <input type="number" step="0.5" min={1} max={168} value={weeklyHours} onChange={(e) => setWeeklyHours(e.target.value)} className={`${inputClass} mt-1`} />
          </label>
          <label className="text-sm font-medium text-stone-700">
            Daily threshold (hours)
            <input type="number" step="0.5" min={1} max={24} value={dailyHours} placeholder="None" onChange={(e) => setDailyHours(e.target.value)} className={`${inputClass} mt-1`} />
          </label>
          <label className="text-sm font-medium text-stone-700">
            Overtime rate
            <input type="number" step="0.25" min={1} max={3} value={multiplier} onChange={(e) => setMultiplier(e.target.value)} className={`${inputClass} mt-1`} />
          </label>
          <label className="text-sm font-medium text-stone-700">
            Pay week starts
            <select value={weekStartsOn} onChange={(e) => setWeekStartsOn(Number(e.target.value))} className={`${selectClass} mt-1 block w-full`}>
              {DAYS.map((day, index) => (
                <option key={day} value={index}>
                  {day}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="text-sm text-stone-500">
          Connecticut and federal law use a 40 hour weekly threshold and no daily threshold. Leave the daily field empty unless you pay daily overtime by policy.
        </p>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={requireReason} onChange={(e) => setRequireReason(e.target.checked)} className="h-4 w-4 accent-leaf-700" />
          Require a reason when a director edits time
        </label>
        <div className="flex justify-end">
          <Button type="submit" disabled={busy}>
            {busy ? "Saving" : "Save settings"}
          </Button>
        </div>
      </form>
    </Card>
  );
}
